'use client'

import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { onEventSetPendingStatus } from './helpers/onEventSetPendingStatus'
import { PendingStatusEventTriggers } from './constants/PendingStatusTriggers'

/**
 * @namespace
 * @description provides the action status of a form with the given formId
 * Initially, pending is false. When a form was successfully submitted,
 * pending is set to true. On the following events, the pending state is set to false
 * - when an action failed
 * - when an action was successful
 * - when the result of an action was unknown, but the action is not running anymore
 *
 * The advantage of using this hook over react's useFormStatus hook, is that
 * this hook can be used outside of the form and useFormStatus can not.
 *
 * @listens SubmitSuccess
 * @listens ActionSuccess
 * @listens ActionFailed
 * @listens ActionEnded
 *
 * */
export const usePendingStatus = (formId: string) => {
    const [pending, setPending] = useState(false)
    useEffect(() => {
        if (!formId) {
            return
        }
        const controller = new AbortController()
        const signal = controller.signal

        const sharedArguments: [string, Dispatch<SetStateAction<boolean>>, AbortSignal] = [formId, setPending, signal]

        onEventSetPendingStatus(PendingStatusEventTriggers.SUBMIT_SUCCESS, ...sharedArguments)
        onEventSetPendingStatus(PendingStatusEventTriggers.ACTION_FAILED, ...sharedArguments)
        onEventSetPendingStatus(PendingStatusEventTriggers.ACTION_SUCCESS, ...sharedArguments)
        onEventSetPendingStatus(PendingStatusEventTriggers.ACTION_ENDED, ...sharedArguments)

        return () => {
            controller.abort()
        }
    }, [])

    return { pending }
}
